//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import axios from './axios';
import { getSafeToken } from './securityUtils';
import { User } from 'oidc-client-ts';
import {
  UserGroupDto,
  CreateGroupRequest,
  UpdateGroupRequest,
} from '../components/UserGroups/types';

class UserGroupDataService {
  private static instance: UserGroupDataService;
  private tenantBaseUrl: string;
  private getUserContext: () => User;

  private constructor(tenantBaseUrl: string, getUserContext: () => User) {
    this.tenantBaseUrl = tenantBaseUrl;
    this.getUserContext = getUserContext;
  }

  public static getInstance(
    tenantBaseUrl?: string,
    getUserContext?: () => User,
  ): UserGroupDataService {
    if (!UserGroupDataService.instance && tenantBaseUrl && getUserContext) {
      UserGroupDataService.instance = new UserGroupDataService(
        tenantBaseUrl,
        getUserContext,
      );
    } else if (
      UserGroupDataService.instance &&
      tenantBaseUrl &&
      getUserContext
    ) {
      // Re-initialize if URL or context changed (e.g., after logout/login)
      if (
        UserGroupDataService.instance.tenantBaseUrl !== tenantBaseUrl ||
        UserGroupDataService.instance.getUserContext !== getUserContext
      ) {
        UserGroupDataService.instance = new UserGroupDataService(
          tenantBaseUrl,
          getUserContext,
        );
      }
    }
    return UserGroupDataService.instance;
  }

  public static resetInstance(): void {
    UserGroupDataService.instance = null as any;
  }

  private getAuthHeaders(): { Authorization: string; 'Content-Type': string } {
    const user = this.getUserContext();
    const token = getSafeToken(user?.access_token);
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async listGroups(teamspaceId: string): Promise<UserGroupDto[]> {
    try {
      const response = await axios.get<UserGroupDto[]>(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/groups`,
        {
          headers: this.getAuthHeaders(),
        },
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching groups:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch groups',
      );
    }
  }

  async getGroup(teamspaceId: string, groupId: string): Promise<UserGroupDto> {
    try {
      const response = await axios.get<UserGroupDto>(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/groups/${groupId}`,
        {
          headers: this.getAuthHeaders(),
        },
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching group:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch group');
    }
  }

  async createGroup(
    teamspaceId: string,
    request: CreateGroupRequest,
  ): Promise<UserGroupDto> {
    try {
      const response = await axios.post<UserGroupDto>(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/groups`,
        request,
        {
          headers: this.getAuthHeaders(),
        },
      );
      return response.data;
    } catch (error: any) {
      console.error('Error creating group:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to create group',
      );
    }
  }

  async updateGroup(
    teamspaceId: string,
    groupId: string,
    request: UpdateGroupRequest,
  ): Promise<UserGroupDto> {
    try {
      const response = await axios.put<UserGroupDto>(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/groups/${groupId}`,
        request,
        {
          headers: this.getAuthHeaders(),
        },
      );
      return response.data;
    } catch (error: any) {
      console.error('Error updating group:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to update group',
      );
    }
  }

  async deleteGroup(teamspaceId: string, groupId: string): Promise<void> {
    try {
      await axios.delete(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/groups/${groupId}`,
        {
          headers: this.getAuthHeaders(),
        },
      );
    } catch (error: any) {
      console.error('Error deleting group:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to delete group',
      );
    }
  }

  async updateOverridePolicy(
    teamspaceId: string,
    groupId: string,
    allowOverride: boolean,
  ): Promise<UserGroupDto> {
    try {
      const response = await axios.patch<UserGroupDto>(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/groups/${groupId}/override-policy`,
        { allowUserOverride: allowOverride },
        {
          headers: this.getAuthHeaders(),
        },
      );
      return response.data;
    } catch (error: any) {
      console.error('Error updating override policy:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to update override policy',
      );
    }
  }

  async createUserOverride(
    teamspaceId: string,
    integrationName: string,
    overrideFields: Record<string, any>,
  ): Promise<void> {
    try {
      await axios.put(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/integrations/${integrationName}/overrides`,
        { overrideFields },
        {
          headers: this.getAuthHeaders(),
        },
      );
    } catch (error: any) {
      console.error('Error creating user override:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to create user override',
      );
    }
  }

  async listTeamspaceIntegrations(teamspaceId: string): Promise<any[]> {
    try {
      const response = await axios.get<string>(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/integrations`,
        {
          headers: this.getAuthHeaders(),
        },
      );

      // Response is a JSON string containing integrations array
      let data: any = response.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (parseError) {
          console.error('Failed to parse integrations response:', parseError);
          return [];
        }
      }

      return data.integrations || [];
    } catch (error: any) {
      console.error('Error fetching integrations:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch integrations',
      );
    }
  }

  async listTeamspaceUsers(teamspaceId: string): Promise<
    Array<{
      id: string;
      username: string;
      fullname: string;
      keycloakId: string;
    }>
  > {
    try {
      const response = await axios.get<
        Array<{
          id: string;
          username: string;
          fullname: string;
          keycloakId: string;
          role: string;
        }>
      >(`${this.tenantBaseUrl}/teamspaces/${teamspaceId}/users`, {
        headers: this.getAuthHeaders(),
      });

      return response.data;
    } catch (error: any) {
      console.error('Error fetching users:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch users');
    }
  }

  async assignIntegrationToGroup(
    teamspaceId: string,
    integrationName: string,
    groupId: string,
  ): Promise<any> {
    try {
      const response = await axios.put(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/integrations/${integrationName}/groups/${groupId}`,
        {},
        {
          headers: this.getAuthHeaders(),
        },
      );

      return response.data;
    } catch (error: any) {
      console.error('Error assigning integration to group:', error);
      throw new Error(
        error.response?.data?.message ||
          'Failed to assign integration to group',
      );
    }
  }

  async unassignIntegrationFromGroup(
    teamspaceId: string,
    integrationName: string,
    groupId: string,
  ): Promise<void> {
    try {
      await axios.delete(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/integrations/${integrationName}/groups/${groupId}`,
        {
          headers: this.getAuthHeaders(),
        },
      );
    } catch (error: any) {
      console.error('Error unassigning integration from group:', error);
      throw new Error(
        error.response?.data?.message ||
          'Failed to unassign integration from group',
      );
    }
  }

  async getAccessibleIntegration(teamspaceId: string): Promise<any | null> {
    try {
      const response = await axios.get(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/integrations/accessible`,
        {
          headers: this.getAuthHeaders(),
        },
      );
      if (response.status === 204) {
        return null;
      }
      return response.data;
    } catch (error: any) {
      console.error('Error fetching accessible integration:', error);
      throw new Error(
        error.response?.data?.message ||
          'Failed to fetch integration configuration',
      );
    }
  }
}

export default UserGroupDataService;
